import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { MoreVertical, type LucideIcon } from "lucide-react";
import { createPortal } from "react-dom";

export interface ItemAction {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface ItemActionsMenuProps {
  actions: ItemAction[];
  ariaLabel?: string;
  align?: "left" | "right";
}

interface MenuPosition {
  top: number;
  left: number;
  openUp: boolean;
}

/**
 * Compact ⋯ menu for video workspace actions.
 * The panel is portaled to document.body so card overflow never clips it.
 */
const ItemActionsMenu: React.FC<ItemActionsMenuProps> = ({
  actions,
  ariaLabel = "Actions",
  align = "right",
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const panelHeight = panel?.offsetHeight ?? 180;
      const panelWidth = panel?.offsetWidth ?? 184;
      const gap = 4;
      const openUp =
        rect.bottom + gap + panelHeight > window.innerHeight &&
        rect.top > panelHeight + gap;

      let left = align === "right" ? rect.right - panelWidth : rect.left;
      left = Math.min(Math.max(8, left), window.innerWidth - panelWidth - 8);

      setPosition({
        top: openUp ? rect.top - gap - panelHeight : rect.bottom + gap,
        left,
        openUp,
      });
    };

    updatePosition();
    // Re-measure once the panel has painted so height is accurate.
    const frame = requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (actions.length === 0) return null;

  return (
    <div className={`item-actions-menu${open ? " is-open" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="item-actions-menu__trigger"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        title={ariaLabel}
        onClick={event => {
          event.stopPropagation();
          setOpen(prev => !prev);
        }}
      >
        <MoreVertical size={16} />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="item-actions-menu__panel item-actions-menu__panel--portal"
            role="menu"
            style={
              position
                ? {
                    top: position.top,
                    left: position.left,
                  }
                : { visibility: "hidden", top: 0, left: 0 }
            }
          >
            {actions.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  type="button"
                  role="menuitem"
                  className={`item-actions-menu__item${
                    action.danger ? " is-danger" : ""
                  }`}
                  disabled={action.disabled}
                  onClick={event => {
                    event.stopPropagation();
                    if (action.disabled) return;
                    setOpen(false);
                    action.onClick();
                  }}
                >
                  {Icon && <Icon size={14} aria-hidden />}
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ItemActionsMenu;
